
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IPresignedURLResponse, IResponseMessage, IRole, IUser } from '../../models';
import { AuthService, CommonService, ContentsService, SettingsService, ToasterService, UsersService } from '../../services';
import { HttpErrorResponse } from '@angular/common/http';
import { Constants, GlobalSettings } from '../../common';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { AvatarComponent } from '../avatar';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-add-user',
  imports: [
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    AvatarComponent,
    MatIconModule
  ],
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss'
})
export class AddUserComponent implements OnInit, AfterViewInit {

  limits = GlobalSettings.characterLimits;

  @Input() currentUser: IUser = {} as IUser;
  @Input() isUpdateUser = false;
  @Input() isProfileEdit = false;

  @Output() closeEvent = new EventEmitter<string>();

  @ViewChild('nameInput') nameInput!: ElementRef<HTMLInputElement>;

  editUserForm!: FormGroup;
  currentUserImage: File | null = null;

  roles: IRole[] = [];

  constructor(private fb: FormBuilder, public commonService: CommonService, private userService: UsersService, private authService: AuthService,
    private toaster: ToasterService, private settingsService: SettingsService, private contentsService: ContentsService) { }

  ngOnInit(): void {
    this.initializeForms();
    this.getAllRoles();
  }

  ngAfterViewInit() {
    // Focus and select all text in the name input field
    this.nameInput.nativeElement.focus();
    // this.nameInput.nativeElement.select();
  }

  initializeForms() {
    const currentUser: IUser = this.currentUser ? JSON.parse(JSON.stringify(this.currentUser)) : {} as IUser;
    const roleValidators = currentUser.is_admin ? [] : [Validators.required];
    this.editUserForm = this.fb.group({
      id: [currentUser.id],
      full_name: [currentUser.full_name, [Validators.required]],
      email: [currentUser.email, [Validators.required, Validators.email, Validators.pattern(Constants.emailPattern)]],
      role: [currentUser.role_id, roleValidators],
      role_name: [currentUser.is_admin ? 'Admin' : ''],
      profile_image: [currentUser.profile_image]
    });

    if (currentUser.is_admin || this.isProfileEdit) {
      this.editUserForm.get('email')?.disable();
      this.editUserForm.get('role')?.disable();
      this.editUserForm.get('role_name')?.disable();
    }
  }

  getAllRoles() {
    this.settingsService.getAllUserRoles().subscribe({
      next: (role: IRole[]) => {
        this.roles = role?.length > 0 ? role : [];
      },
      error: (error) => {
        console.error('Error fetching permissions:', error);
      }
    })
  }

  getFormHeight() {
    return window.innerHeight - 280;
  }

  onFormSubmit() {
    const noEmptySpace = this.editUserForm.value?.full_name?.trim();
    if (this.editUserForm.valid && noEmptySpace) {
      if (this.currentUserImage) {
        this.uploadImage();
      }
      else {
        if (this.isProfileEdit) {
          this.updateProfile();
        }
        else {
          this.addOrUpdateUser();
        }
      }
    }
    else {
      if (!this.editUserForm.valid) {
        this.toaster.warning(Constants.notifyMessages.invalidForm);
      }
      else if (!noEmptySpace) {
        this.toaster.warning(Constants.notifyMessages.emptyValues);
      }
      Object.values(this.editUserForm.controls).forEach(control => {
        control.markAsTouched();
      });
    }
  }

  uploadImage() {
    if (this.currentUserImage != null) {
      this.commonService.isLoading = true;
      // 1. Get the presigned URL for the image file
      this.contentsService.getPresignedURLForS3(this.currentUserImage.name, this.currentUserImage.type, this.currentUserImage.size)
        .subscribe({
          next: (presignedData: IPresignedURLResponse) => {
            if (!presignedData?.url) {
              throw new Error('No presigned URL received');
            }

            if (this.currentUserImage) {
              // 2. Upload the image file to S3 (no progress tracking)
              this.contentsService.uploadFileToS3(presignedData.url, this.currentUserImage)
                .subscribe({
                  next: () => {
                    // 3. After successful upload, update the screen with the image URL
                    const imageUrl = presignedData.url.split('?')[0]; // Remove query params if any
                    this.currentUser.profile_image = imageUrl;
                    this.editUserForm.patchValue({ profile_image: imageUrl }); // Update the form with the file url
                    if (this.isProfileEdit) {
                      this.updateProfile();
                    }
                    else {
                      this.addOrUpdateUser();
                    }
                  },
                  error: (uploadError: HttpErrorResponse) => {
                    this.commonService.isLoading = false;
                    console.error('Image upload failed:', uploadError);
                    this.toaster.error(uploadError?.error?.error ? uploadError.error.message : Constants.notifyMessages.commonErrorLoading)
                  }
                });
            }
          },
          error: (urlError: HttpErrorResponse) => {
            this.commonService.isLoading = false;
            this.toaster.error(urlError?.error?.message ? urlError.error.message : Constants.notifyMessages.commonErrorLoading);
          }
        });
    }
    else {
      if (this.isProfileEdit) {
        this.updateProfile();
      }
      else {
        this.addOrUpdateUser();
      }
    }
  }

  addOrUpdateUser() {
    this.commonService.isLoading = true;
    if (this.isUpdateUser) {
      this.userService.updateUser(this.currentUser.id, this.editUserForm.value).subscribe({
        next: (response: IResponseMessage) => {
          // console.log(response);
          this.commonService.isLoading = false;
          this.closeEvent.emit('success');
          this.toaster.success(response?.message ? response.message : Constants.notifyMessages.updateSuccessUser);
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.isLoading = false;
          console.log(error);
          this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.updateErroruser);
        }
      })
    }
    else {
      this.userService.addUser(this.editUserForm.value).subscribe({
        next: (response: IResponseMessage) => {
          // console.log(response);
          this.commonService.isLoading = false;
          this.closeEvent.emit('success');
          this.toaster.success(response?.message ? response.message : Constants.notifyMessages.addSuccessUser);
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.isLoading = false;
          console.log(error);
          this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.addErrorUser);
        }
      })
    }
  }

  updateProfile() {
    this.commonService.updateProfileDetails(this.editUserForm.value).subscribe({
      next: (response: IResponseMessage) => {
        // console.log(response);
        this.commonService.isLoading = false;
        this.closeEvent.emit('success');
        this.commonService.refreshProfileDetails.next();
        this.toaster.success(response?.message ? response.message : Constants.notifyMessages.updateSuccessUser);
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.isLoading = false;
        console.log(error);
        this.toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.updateErroruser);
      }
    })
  }

  onAvatarImageSelected(file: File) {
    this.currentUserImage = file;
    // console.log(this.currentUserImage)
    const reader = new FileReader();
    reader.onload = () => {
      this.currentUser.profile_image = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  onAvatarImageDeleted() {
    this.currentUserImage = null;
    this.currentUser.profile_image = ''
    this.editUserForm.patchValue({ profile_image: '' }); // Update the form with ''
  }

  cancelClick() {
    this.closeEvent.emit('close');
  }

  deleteUserProfile() {
    const result = this.commonService.openDialog({
      type: 'confirmation',
      title: 'Confirm',
      subtitle: Constants.notifyMessages.deleteProfileConfirm,
      yesButtonText: Constants.buttonTexts.confirmButtonText,
      noButtonText: 'No'
    });
    result.then(value => {
      if (value) {
        this.commonService.isLoading = true;
        this.userService.deleteUserProfile().subscribe({
          next: (response: IResponseMessage) => {
            // console.log(response);
            this.commonService.isLoading = false;
            this.authService.logout();
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
            this.toaster.error(error?.message ? error.message : 'An error occurred!')
            this.commonService.isLoading = false;
          }
        })
      }
    })
  }
}
